import { useState, useEffect, useRef } from 'react'
import {
  reportingApiClient,
  type VoicePipelineDashboardResponse,
} from '@/lib/api/reporting.client'
import { useAppAuth } from '@/contexts/app-auth.context'

type CompanyScope = 'all' | 'selected'

export function useVoicePipelineData() {
  const { currentCompany, isAppContextLoading, isInitialized } = useAppAuth()
  const [dashboardData, setDashboardData] =
    useState<VoicePipelineDashboardResponse | null>(null)
  const [isFetching, setIsFetching] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [dateRange, setDateRange] = useState<string>('48h')
  const [companyScope, setCompanyScope] = useState<CompanyScope>('all')
  const [language, setLanguageState] = useState<string>('')
  const [customStartDate, setCustomStartDate] = useState<string>('')
  const [customEndDate, setCustomEndDate] = useState<string>('')
  const [appliedCustomDates, setAppliedCustomDates] = useState<{
    start: string
    end: string
  } | null>(null)
  const lastFetchedKeyRef = useRef<string | null>(null)

  const effectiveCompanyId =
    companyScope === 'selected' ? currentCompany?.id : undefined

  const fetchDashboard = async (
    explicitDateRange?: string,
    customDates?: { start: string; end: string },
    explicitCompanyId?: string | undefined,
    explicitLanguage?: string,
  ) => {
    const activeDateRange = explicitDateRange ?? dateRange
    const datesToUse = customDates ?? appliedCustomDates
    const companyId =
      explicitCompanyId !== undefined ? explicitCompanyId : effectiveCompanyId
    const activeLang =
      explicitLanguage !== undefined ? explicitLanguage : language

    const getFetchKey = () => {
      const companyKey = companyId || 'all'
      const langKey = activeLang || 'all'
      if (activeDateRange === 'custom') {
        if (!datesToUse?.start || !datesToUse?.end) return null
        return `${companyKey}:${langKey}:${activeDateRange}:${datesToUse.start}:${datesToUse.end}`
      }
      return `${companyKey}:${langKey}:${activeDateRange}`
    }

    const currentFetchKey = getFetchKey()
    if (!currentFetchKey) return
    if (lastFetchedKeyRef.current === currentFetchKey) return

    setIsFetching(true)
    setError(null)

    try {
      let startDate: string
      let endDate: string

      if (activeDateRange === 'custom' && datesToUse) {
        startDate = new Date(datesToUse.start).toISOString()
        const endOfDay = new Date(datesToUse.end)
        endOfDay.setHours(23, 59, 59, 999)
        endDate = endOfDay.toISOString()
      } else {
        const end = new Date()
        const start = new Date()
        switch (activeDateRange) {
          case '48h':
            start.setHours(end.getHours() - 48)
            break
          case '7d':
            start.setDate(end.getDate() - 7)
            break
          case '30d':
            start.setDate(end.getDate() - 30)
            break
          case '90d':
            start.setDate(end.getDate() - 90)
            break
        }
        startDate = start.toISOString()
        endDate = end.toISOString()
      }

      const params: Record<string, string> = { startDate, endDate }
      if (companyId) params.companyId = companyId
      if (activeLang) params.language = activeLang

      const data = await reportingApiClient.getVoicePipelineDashboard(params)
      setDashboardData(data)
      lastFetchedKeyRef.current = currentFetchKey
    } catch (err) {
      setError(
        err instanceof Error ? err.message : 'Failed to load pipeline data',
      )
      lastFetchedKeyRef.current = null
    } finally {
      setIsFetching(false)
    }
  }

  useEffect(() => {
    if (isInitialized && !isAppContextLoading) {
      lastFetchedKeyRef.current = null
      fetchDashboard()
    }
  }, [isInitialized, isAppContextLoading, currentCompany?.id, companyScope])

  const handleDateRangeChange = (newDateRange: string) => {
    if (newDateRange !== 'custom') {
      setCustomStartDate('')
      setCustomEndDate('')
      setAppliedCustomDates(null)
    }
    setDateRange(newDateRange)
    fetchDashboard(newDateRange)
  }

  const handleCompanyScopeChange = (scope: CompanyScope) => {
    // All-companies only supports 48h/7d (server clamps wider ranges)
    if (scope === 'all' && dateRange !== '48h' && dateRange !== '7d') {
      setCustomStartDate('')
      setCustomEndDate('')
      setAppliedCustomDates(null)
      setDateRange('48h')
    }
    setCompanyScope(scope)
    // The useEffect will trigger a re-fetch due to companyScope dependency
  }

  const handleCustomDateRangeApply = () => {
    if (customStartDate && customEndDate) {
      const start = new Date(customStartDate)
      const end = new Date(customEndDate)
      if (start > end) {
        setError('Start date must be before end date')
        return
      }
      const dates = { start: customStartDate, end: customEndDate }
      setAppliedCustomDates(dates)
      setDateRange('custom')
      lastFetchedKeyRef.current = null
      fetchDashboard('custom', dates)
    }
  }

  const handleLanguageChange = (lang: string) => {
    setLanguageState(lang)
    lastFetchedKeyRef.current = null
    fetchDashboard(undefined, undefined, undefined, lang)
  }

  const handleCustomDateRangeCancel = () => {
    setCustomStartDate('')
    setCustomEndDate('')
    setAppliedCustomDates(null)
    if (dateRange === 'custom') {
      setDateRange('7d')
      fetchDashboard('7d')
    }
  }

  return {
    dashboardData,
    isFetching,
    error,
    dateRange,
    setDateRange: handleDateRangeChange,
    companyScope,
    setCompanyScope: handleCompanyScopeChange,
    customStartDate,
    setCustomStartDate,
    customEndDate,
    setCustomEndDate,
    appliedCustomDates,
    handleCustomDateRangeApply,
    handleCustomDateRangeCancel,
    language,
    setLanguage: handleLanguageChange,
  }
}
